import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { QuestionCollectionRequest } from '@app/dashboard/shared/models/audit-collection-request';
import { Question, QuestionCollectionResponse } from '@app/dashboard/shared/models/audit-collection-response';
import { AuditProgress } from '@app/dashboard/shared/models/audit-progress';
import { AuditsService } from '@app/dashboard/shared/services/audits.service';

import { catchError, of, switchMap } from 'rxjs';

@Component({
	selector: 'kpa-perform-audit',
	templateUrl: './perform-audit.component.html',
	styleUrl: './perform-audit.component.scss',
})
export class PerformAuditComponent implements OnChanges {
	public defaultQuestionPerPage: number = 1;
	public currentQuestion: number = 1;
	public previousQuestion: number = 1;
	public perPageQuestionIds: number[] = [1];

	public auditProgress: AuditProgress;
	public auditQuestionCollection: QuestionCollectionResponse;
	@Input() currentAuditId: number;
	@Output() onClose = new EventEmitter();
	@Input() showAudit: boolean = false;
	@Input() isFromPlatform: boolean = false;
	public isAudit: boolean;
	@Output() onTaskComplete: EventEmitter<boolean> = new EventEmitter<boolean>();
	constructor(private auditsService: AuditsService) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.showAudit && changes.showAudit.currentValue === true) {
			this.initializeAudit();
		}
	}

	private initializeAudit(): void {
		this.auditsService
			.getProgress(this.currentAuditId)
			.pipe(
				switchMap((result) => {
					this.auditProgress = result;
					this.currentQuestion = this.auditProgress.firstIncompleteQuestion;
					this.updatePerPageQuestionIds(this.currentQuestion);
					let request: QuestionCollectionRequest = {
						pageNumber: this.auditProgress.firstIncompleteQuestion,
						questionsPerPage: this.defaultQuestionPerPage,
						scheduledAuditId: this.currentAuditId,
					};
					return this.auditsService.fetchQuestionCollection(request);
				}),
				catchError((error) => {
					console.error('Error fetching audit progress or questions:', error);
					return of(null);
				})
			)
			.subscribe((result) => {
				if (result) {
					this.auditQuestionCollection = this.transformResponse(result);
					this.isAudit = true;
				}
			});
	}
	createDefaultQuestion(): Question {
		return {
			questionId: null,
			questionText: null,
			questionDescription: null,
			referenceUrl: null,
			showReferenceUrl: null,
			responseType: null,
			shortResponse: null,
			multipleChoice: null,
			multipleAnswer: null,
			responses: [],
			responseCheckItems: [],
			scheduledAuditId: null,
			notAllowFile: null,
			notAllowLightbox: null,
			notAllowComments: null,
			allowNaResponse: null,
			comments: null,
			selectedResponseIds: [],
			selectedResponseId: null,
			responseText: null,
			orderNumber: null,
			imageUrl: null,
			showQuestionImage: null,
			answerAttachments: [],
		};
	}
	populateQuestion(data: Partial<Question>): Question {
		const defaultQuestion = this.createDefaultQuestion();
		return { ...defaultQuestion, ...data };
	}
	transformResponse(data: any): QuestionCollectionResponse {
		return {
			scheduledAuditId: data.scheduledAuditId ?? null,
			questions: data.questions.map((q: Partial<Question>) => this.populateQuestion(q)),
		};
	}
	onPageActionChange(event: any) {
		this.currentQuestion = event.currentQuestionNumber;
		this.updatePerPageQuestionIds(event.nextQuestionNumber);
		this.auditsService.insertAnswer(event.answers).subscribe((result) => {
			if (result) {
				let request: QuestionCollectionRequest = {
					pageNumber: event.nextQuestionNumber,
					questionsPerPage: this.defaultQuestionPerPage,
					scheduledAuditId: event.answers.scheduledAuditId,
				};
				this.auditsService.fetchQuestionCollection(request).subscribe((result) => {
					this.auditQuestionCollection = this.transformResponse(result);
					this.isAudit = true;
					this.currentQuestion = event.nextQuestionNumber;
				});
			}
		});
	}
	updateItemsPerPage(event: any) {
		this.auditsService.insertAnswer(event.answers).subscribe((result) => {
			if (result) {
				this.currentQuestion = 1;
				this.defaultQuestionPerPage = event.itemsPerPage;
				this.updatePerPageQuestionIds(this.currentQuestion);
				let request: QuestionCollectionRequest = {
					pageNumber: this.currentQuestion,
					questionsPerPage: this.defaultQuestionPerPage,
					scheduledAuditId: this.currentAuditId,
				};
				this.auditsService.fetchQuestionCollection(request).subscribe((result) => {
					this.auditQuestionCollection = this.transformResponse(result);
					this.isAudit = true;
				});
			}
		});
	}
	submitAudit(event: any) {
		this.currentQuestion = event.currentQuestionNumber;
		this.updatePerPageQuestionIds(event.nextQuestionNumber);
		this.auditsService.insertAnswer(event.answers).subscribe((result) => {
			if (result) {
				let request: QuestionCollectionRequest = {
					pageNumber: event.nextQuestionNumber,
					questionsPerPage: this.defaultQuestionPerPage,
					scheduledAuditId: event.answers.scheduledAuditId,
				};
				this.auditsService.fetchQuestionCollection(request).subscribe((result) => {
					this.auditQuestionCollection = this.transformResponse(result);
					this.isAudit = true;
					this.auditsService.getStatus(this.auditQuestionCollection.scheduledAuditId).subscribe((result) => {
						this.isAudit = true;
						if (result) {
							this.auditsService.submit(this.auditQuestionCollection.scheduledAuditId).subscribe((result) => {
								if (result) {
									this.closeDialog();
									if (!this.isFromPlatform) {
										this.onTaskComplete.emit(true);
									}
								}
							});
						}
					});
				});
			}
		});
	}
	updatePerPageQuestionIds(upcominQuestion: number) {
		if (upcominQuestion < 1 || this.defaultQuestionPerPage < 1) {
			this.perPageQuestionIds = [];
			return;
		}
		const startIndex = (upcominQuestion - 1) * this.defaultQuestionPerPage + 1;
		const endIndex = startIndex + this.defaultQuestionPerPage - 1;
		this.perPageQuestionIds = [];
		for (let i = startIndex; i <= endIndex; i++) {
			this.perPageQuestionIds.push(i);
		}
	}
	closeDialog() {
		this.isAudit = false;
		this.defaultQuestionPerPage = 1;
		this.currentQuestion = 1;
		this.perPageQuestionIds = [1];
		this.onClose.emit();
	}
}
